// Hold, cancel and reopen on the requisition (lane B2 S10; CR-037, CR-038, CR-056, CR-057, CR-058): "HM or TA team may
// request hold/cancel/reopen; HRBP approves; TA Manager notified." The three kinds share one request dialog and one
// approval card (DS DC-73): the dialog *Request hold / cancellation / reopen* (DS row 60) asks with a reason from the
// published list and the requester's rationale (ReasonBox, row 77); a cancellation says why, picks its *Reporting
// category* and writes its *Final notes*, and waits on *All candidates dispositioned* (Gate below, row 67), with the
// count and a link to the disposition worklist ("Cancellation requires reason, HRBP approval, TA Manager notice, final
// notes/reporting category and all candidates dispositioned."). The request then waits on the requisition's HR Business
// Partner under the *HRBP approval* section, the card titled from the request (its kind · *Requested*, a *Pending* pill,
// the requester and the date: F-9, a waiting hold never reads as an applied one): Approve applies it at once (a hold
// pauses the clocks now), Reject refuses it (a ghost, DS DC-23 "Return, Reject, Decline, Cancel are ghost."; Return
// stays on the Hiring Request's decisions). Both are drawn only on the server's decide flag, for the named HR Business
// Partner (or the stand-in the server admits) and for the Admin, each under their own name (the Admin is never labelled
// *Override*: since DC-228 an Admin acts by the ordinary path, and the receipt carries no override); a person never
// decides their own request (the server refuses and the card shows its red banner). After a decision the
// requisition shows *TA Manager notified* with its date, and a refused request reads *Rejected* on red (DS DC-18).
// A hold request has no status word of its own (B1 Q-M2): only the card. Resuming a hold has no word of his yet, so it
// stays the V1 control beside the page (Requisitions.tsx).
// The reason lists are the Admin's published lists, read through `config.options` (`reasons:requisition_hold`,
// `reasons:cancel_category`, `reasons:reopen`); a list refused as not yet published offers nothing to choose.

import { useState } from "react";
import {
  configOptions, requisitionApproveReopen, requisitionDecideStateChange, requisitionRequestStateChange, type ConfigOptionsOutput,
  type RequisitionFull, type StateChangeView,
} from "@/api/tools";
import { useWrite } from "@/api/useTool";
import {
  ACTION_LABELS, Button, Card, COMPENSATION_REVIEW_STATUS, DIALOG_LABELS, Dialog, Field, G1_LABELS, Gate, JOURNEY_LABELS, KeyValue, MenuButton,
  Paragraph, Person, Pill, REQUISITION_STATUS, ReasonBox, Recorded, SCHEDULING_STATUS, STATE_BANNER_LABELS, STATE_CHANGE_DECISION,
  SectionTitle, Select, Stack, TextArea, formatDate, type KeyValueItem, type SelectOption, type StateBannerProps,
} from "@/mc";
import { useSession } from "@/session/useSession";
import { requisitionReads, useSettledRead } from "./data";
import { GOV_LABELS, GOV_RULES } from "./labels";

export type StateChangeKind = "hold" | "cancel" | "reopen";

export const STATE_CHANGE_KINDS: readonly StateChangeKind[] = ["hold", "cancel", "reopen"];

/** The kind a request asks for, in his words. */
export const KIND_WORD: Readonly<Record<StateChangeKind, string>> = {
  hold: ACTION_LABELS.hold, cancel: GOV_LABELS.cancellation, reopen: GOV_LABELS.reopen,
};

/** The waiting request's title: its kind and his status word for a thing asked and not yet done (*Requested*, the
 * first word of his request chains), never the applied state's word. */
export const requestedTitle = (kind: StateChangeKind) => `${KIND_WORD[kind]} · ${SCHEDULING_STATUS.requested}`;

/** The request waits on the HR Business Partner's decision: his *Pending*. */
const PENDING = COMPENSATION_REVIEW_STATUS.pending;

/** Who signs in, as the card needs it: the person, and whether they hold the Admin's capability. */
export interface Viewer {
  id: string | null;
  roles: readonly string[];
}

/** The decision the viewer may make on the waiting request, from the server's decide flag (`requisition.open`'s
 * `can.decide_state_change`, or `can.approve_reopen` on a reopen record's door): none; as the HR Business Partner (the
 * requisition's named HRBP, or the stand-in the server admits); or as the Admin, under the Admin's own name and with no
 * Override label (ruling of 12 Oct: no override holder decides here; the receipt carries no override). */
export function decisionOf(r: RequisitionFull, waiting: Waiting, viewer: Viewer | null | undefined): "none" | "hrbp" | "admin" {
  const flag = waiting.scr ? r.can.decide_state_change : r.can.approve_reopen;
  if (!flag) return "none";
  return viewer && viewer.roles.includes("admin") && viewer.id !== r.hrbp_id ? "admin" : "hrbp";
}

/** His rule sentence for each kind. */
export const KIND_RULE: Readonly<Record<StateChangeKind, string>> = {
  hold: GOV_RULES.holdRecords, cancel: GOV_RULES.cancellationRequires, reopen: GOV_RULES.reopenPreserves,
};

const isKind = (value: string | null | undefined): value is StateChangeKind => value === "hold" || value === "cancel" || value === "reopen";

/** The kinds this viewer may ask for now (the server's `can`). */
export function askable(r: RequisitionFull): StateChangeKind[] {
  return STATE_CHANGE_KINDS.filter((kind) => r.can[`request_${kind}`]);
}

/** The disposition worklist a cancellation waits on (V1's Offer & close section until lane B9 moves it). */
export const worklistHref = (r: RequisitionFull) => `#requisitions/${r.id}/offer`;

/** The published reason list each kind asks with (`reasons:<name>`). */
export const KIND_LIST: Readonly<Record<StateChangeKind, string>> = {
  hold: "requisition_hold", cancel: "cancel_category", reopen: "reopen",
};

/** A published reason list as Select options (null: not published or refused, so nothing to choose). */
export function optionsOf(list: ConfigOptionsOutput | null | undefined): SelectOption[] {
  return (list?.options ?? []).map((option) => ({ value: option.slug, label: option.label }));
}

/** The kind's published list, read while its dialog is open; a refusal (not yet published) reads as no list. */
function useKindList(kind: StateChangeKind, enabled: boolean): ConfigOptionsOutput | null {
  const read = useSettledRead(
    ["config-options", "reasons", KIND_LIST[kind]],
    () => configOptions({ kind: "reasons", name: KIND_LIST[kind] }),
    { enabled, staleTime: 5 * 60_000 },
  );
  return read.data ?? null;
}

/** The header's one control for the three kinds: a menu of the kinds the viewer may ask for. */
export function StateChangeMenu({ r, onAsk }: { r: RequisitionFull; onAsk: (kind: StateChangeKind) => void }) {
  const kinds = askable(r);
  if (!kinds.length) return null;
  return (
    <MenuButton
      label={DIALOG_LABELS.requestHoldCancellationReopen}
      items={kinds.map((kind) => ({ label: KIND_WORD[kind], onSelect: () => onAsk(kind), hs: `req.ask-${kind}` }))}
      
    />
  );
}

/** The request dialog. Opened from the URL (`?action=state-change&kind=…`), so a frame can show it. */
export function StateChangeDialog({ r, kind, open, onClose }: { r: RequisitionFull; kind: string | null; open: boolean; onClose: () => void }) {
  if (!isKind(kind) || !r.can[`request_${kind}`]) return null;
  return <RequestForm key={`${kind}-${r.row_version ?? 0}`} r={r} kind={kind} open={open} onClose={onClose} />;
}

function RequestForm({ r, kind, open, onClose }: { r: RequisitionFull; kind: StateChangeKind; open: boolean; onClose: () => void }) {
  const ask = useWrite(
    (input: Parameters<typeof requisitionRequestStateChange>[0], key) => requisitionRequestStateChange(input, key),
    requisitionReads(r.id),
  );
  const list = useKindList(kind, open);
  const [reason, setReason] = useState("");
  const [rationale, setRationale] = useState("");
  const [category, setCategory] = useState("");
  const [finalNotes, setFinalNotes] = useState("");
  const fields = ask.error?.fieldErrors ?? {};
  const open_ = r.undispositioned_count ?? 0;
  const gateRow = { status: open_ > 0 ? "todo" as const : "ok" as const, text: GOV_LABELS.allCandidatesDispositioned, count: open_ || undefined, href: worklistHref(r), hs: "req.ask-worklist" };
  const blocked = kind === "cancel" && open_ > 0 ? { reason: GOV_LABELS.allCandidatesDispositioned } : undefined;
  const submit = () => void ask.mutateAsync({
    requisition_id: r.id,
    kind,
    reason_text: rationale.trim(),
    ...(kind === "cancel"
      ? { reporting_category: category || null, final_notes: finalNotes.trim() || null }
      : { reason_slug: reason || null }),
  }).then(onClose).catch(() => undefined);
  const fieldRefusal = Boolean(fields.reason_text || fields.reason_slug || fields.reporting_category || fields.final_notes);
  return (
    <Dialog
      preset="requestHoldCancellationReopen"
      open={open}
      onClose={onClose}
      dismissLabel={DIALOG_LABELS.cancel}
      primary={{ label: ACTION_LABELS.submit, onClick: submit, busy: ask.isPending, blocked, hs: "req.ask-submit" }}
      refused={ask.error && !fieldRefusal ? { reason: ask.error.remedy || ask.error.message } : undefined}
      
    >
      <Stack gap={16}>
        <div><Pill >{KIND_WORD[kind]}</Pill></div>
        {kind === "cancel" ? (
          <>
            <Field label={G1_LABELS.reason} required hint={G1_LABELS.reasonStored} error={fields.reason_text}>
              <TextArea rows={3} value={rationale} onChange={(event) => setRationale(event.target.value)}  />
            </Field>
            <Field label={G1_LABELS.reportingCategory} required error={fields.reporting_category}>
              <Select options={optionsOf(list)} value={category} onChange={setCategory}  />
            </Field>
            <Field label={G1_LABELS.finalNotes} required error={fields.final_notes}>
              <TextArea rows={3} value={finalNotes} onChange={(event) => setFinalNotes(event.target.value)}  />
            </Field>
            <Gate below rows={[gateRow]} mode="view" />
          </>
        ) : (
          <ReasonBox
            reasons={optionsOf(list)}
            reason={reason}
            onReason={setReason}
            notes={rationale}
            onNotes={setRationale}
            errors={{ reason: fields.reason_slug, notes: fields.reason_text }}
            hs={{ reason: "req.ask-reason", notes: "req.ask-rationale" }}
          />
        )}
        <Paragraph sm>{KIND_RULE[kind]}</Paragraph>
        <Paragraph sm>{STATE_BANNER_LABELS.reopenRequestRule}</Paragraph>
      </Stack>
    </Dialog>
  );
}

/** The request the HR Business Partner decides: the waiting state change request, or a reopen record raised before
 * requests existed (decided through the compatibility door, Approve only). */
export interface Waiting {
  kind: StateChangeKind;
  scr: StateChangeView | null;
  reopenEventId: string | null;
  rationale: string;
  requestedBy: { name: string; id: string; at: string };
}

export function waitingOf(r: RequisitionFull): Waiting | null {
  const scr = r.pending_state_change;
  if (scr) {
    return {
      kind: isKind(scr.kind) ? scr.kind : "reopen", scr, reopenEventId: null, rationale: scr.reason_text,
      requestedBy: { name: scr.requested_by_name ?? "—", id: scr.requested_by_id, at: scr.requested_at },
    };
  }
  const ev = r.pending_reopen;
  if (ev && !ev.state_change_request_id) {
    return {
      kind: "reopen", scr: null, reopenEventId: ev.reopen_event_id ?? ev.id, rationale: ev.reason,
      requestedBy: { name: ev.requested_by_name, id: ev.requested_by_id, at: ev.requested_at },
    };
  }
  return null;
}

/** *HRBP approval*: the waiting request on the requisition, with Approve and Reject for its HR Business Partner. */
export function StateChangeApproval({ r, timeZone }: { r: RequisitionFull; timeZone: string }) {
  const decide = useWrite(
    (input: Parameters<typeof requisitionDecideStateChange>[0], key) => requisitionDecideStateChange(input, key),
    requisitionReads(r.id),
  );
  const door = useWrite(
    (input: { requisition_id: string; reopen_event_id: string }, key) => requisitionApproveReopen(input, key),
    requisitionReads(r.id),
  );
  const { me } = useSession();
  const waiting = waitingOf(r);
  if (!waiting) return null;
  const { scr, reopenEventId } = waiting;
  const decideAs = (decision: "approve" | "reject", note?: string) => (scr
    ? decide.mutateAsync({
      requisition_id: r.id, state_change_request_id: scr.id, expected_version: scr.row_version, decision, ...(note ? { note } : {}),
    })
    : door.mutateAsync({ requisition_id: r.id, reopen_event_id: reopenEventId ?? "" }));
  const error = decide.error ?? door.error;
  return (
    <ApprovalCard
      r={r}
      waiting={waiting}
      viewer={me ? { id: me.person_id, roles: me.roles } : null}
      timeZone={timeZone}
      busy={decide.isPending || door.isPending}
      refusal={error ? error.remedy || error.message : undefined}
      noteError={decide.error?.fieldErrors.note}
      onApprove={() => void decideAs("approve").catch(() => undefined)}
      onReject={(note) => decideAs("reject", note).then(() => true).catch(() => false)}
    />
  );
}

/** The card itself (no reads or writes of its own), under its *HRBP approval* section title. */
export function ApprovalCard({ r, waiting, viewer, timeZone, busy = false, refusal, noteError, onApprove, onReject }: {
  r: RequisitionFull; waiting: Waiting; viewer?: Viewer | null | undefined; timeZone: string; busy?: boolean; refusal?: string | undefined;
  noteError?: string | undefined; onApprove: () => void; onReject: (note: string | undefined) => Promise<boolean>;
}) {
  const [rejecting, setRejecting] = useState(false);
  const [note, setNote] = useState("");
  const { kind, scr, requestedBy } = waiting;
  const decision = decisionOf(r, waiting, viewer);
  const mayDecide = decision !== "none";
  const open_ = r.undispositioned_count ?? 0;
  const gateRow = { status: open_ > 0 ? "todo" as const : "ok" as const, text: GOV_LABELS.allCandidatesDispositioned, count: open_ || undefined, href: worklistHref(r), hs: "req.approval-worklist" };
  const items: KeyValueItem[] = [];
  if (scr?.reason_label) items.push([G1_LABELS.reason, scr.reason_label]);
  items.push([G1_LABELS.rationale, waiting.rationale]);
  if (scr?.reporting_category) items.push([G1_LABELS.reportingCategory, scr.reporting_category_label ?? scr.reporting_category]);
  if (scr?.final_notes) items.push([G1_LABELS.finalNotes, scr.final_notes]);
  const blocked = kind === "cancel" && open_ > 0 ? { reason: GOV_LABELS.allCandidatesDispositioned } : undefined;
  const sendReject = () => void onReject(note.trim() || undefined).then((done) => { if (done) { setNote(""); setRejecting(false); } });
  return (
    <Stack gap={8}>
      <SectionTitle as="h2">{JOURNEY_LABELS.hrbpApproval}</SectionTitle>
      <Card
        title={requestedTitle(kind)}
        pills={<Pill>{PENDING}</Pill>}
        refused={refusal && !rejecting ? { reason: refusal } : undefined}
        actions={mayDecide ? (
          <>
            {scr ? <Button tone="ghost" onClick={() => setRejecting(true)} >{ACTION_LABELS.reject}</Button> : null}
            <Button tone="primary" busy={busy} blocked={blocked} onClick={onApprove} >{ACTION_LABELS.approve}</Button>
          </>
        ) : undefined}
        
      >
        <Stack gap={12}>
          <Person name={requestedBy.name} hueKey={requestedBy.id} sub={formatDate(requestedBy.at, timeZone)} />
          <KeyValue rows items={items} />
          {kind === "cancel" ? <Gate below rows={[gateRow]} mode="view" /> : null}
          <Paragraph sm>{KIND_RULE[kind]}</Paragraph>
        </Stack>
        {scr && mayDecide ? (
          <Dialog
            title={ACTION_LABELS.reject}
            open={rejecting}
            onClose={() => setRejecting(false)}
            dismissLabel={DIALOG_LABELS.cancel}
            primary={{ label: ACTION_LABELS.reject, onClick: sendReject, busy, hs: "req.approval-reject" }}
            refused={refusal && rejecting && !noteError ? { reason: refusal } : undefined}
            
          >
            <Field label={G1_LABELS.reason} hint={G1_LABELS.reasonStored} error={noteError}>
              <TextArea rows={3} value={note} onChange={(event) => setNote(event.target.value)}  />
            </Field>
          </Dialog>
        ) : null}
      </Card>
    </Stack>
  );
}

/** The latest decided request, if the TA Managers were told of it: "TA Manager notified" with its date, and *Rejected*
 * on red when the HR Business Partner refused it. */
export function TaManagerNotified({ r, timeZone }: { r: RequisitionFull; timeZone: string }) {
  const decided = [...(r.state_change_requests ?? [])].reverse().find((scr) => scr.decided_at && scr.status !== "withdrawn");
  if (!decided?.ta_manager_notified_at) return null;
  return (
    <Recorded right={formatDate(decided.ta_manager_notified_at, timeZone)}>
      {decided.status === "rejected"
        ? <><span>{GOV_LABELS.taManagerNotified}</span> <Pill>{STATE_CHANGE_DECISION.rejected}</Pill></>
        : GOV_LABELS.taManagerNotified}
    </Recorded>
  );
}

const activityLine = (ref: string, at: string | null | undefined, timeZone: string) => {
  const name = ref.split("@")[0] ?? ref;
  return at ? `${name} · ${formatDate(at, timeZone)}` : name;
};

/** The StateBanner's details from the requests: the hold's paused and continued activities with their date, the
 * cancellation's reporting category, the closure a reopening reopened, and *Request reopen* for the seats that may. */
export function bannerDetails(r: RequisitionFull, timeZone: string, onRequestReopen: () => void): Partial<StateBannerProps> {
  const approved = (r.state_change_requests ?? []).filter((scr) => scr.status === "approved");
  const hold = [...approved].reverse().find((scr) => scr.kind === "hold" && scr.held_at && !scr.resumed_at);
  const cancel = [...approved].reverse().find((scr) => scr.kind === "cancel");
  const reopened = [...approved].reverse().find((scr) => scr.kind === "reopen");
  const priorCancel = reopened ? [...approved].reverse().find((scr) => scr.kind === "cancel" && (scr.decided_at ?? "") <= (reopened.decided_at ?? "")) : undefined;
  const filled = (r.milestone_history ?? []).filter((m) => m.key === "filled").at(-1);
  return {
    ...(hold ? {
      paused: (hold.paused_activities ?? []).map((ref) => activityLine(ref, hold.held_at, timeZone)),
      continued: (hold.continued_activities ?? []).map((ref) => activityLine(ref, hold.held_at, timeZone)),
    } : {}),
    ...(cancel ? { reportingCategory: cancel.reporting_category_label ?? cancel.reporting_category ?? undefined } : {}),
    ...(priorCancel
      ? { priorClosure: { word: REQUISITION_STATUS.cancelled, at: priorCancel.decided_at ?? undefined } }
      : filled ? { priorClosure: { word: REQUISITION_STATUS.filled, at: filled.at } } : {}),
    ...(r.can.request_reopen ? { onRequestReopen, canReopen: true } : {}),
  };
}
